import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { useId } from 'react';

import useTranslate from 'hooks/useTranslate';
import type {
    CalendarDateRangePresetKey,
    DateRangeKey,
    DateRangePresetKey,
    DateRangeSelectorPresetKey,
} from 'types';

const PRESET_LABEL_KEYS: Record<DateRangePresetKey | CalendarDateRangePresetKey, string> = {
    '7': 'last7Days',
    '30': 'last30Days',
    '60': 'last60Days',
    '90': 'last90Days',
    thisMonth: 'thisMonth',
    '3Months': 'last3Months',
    '6Months': 'last6Months',
    '12Months': 'last12Months',
};

interface DateRangeSelectorProps<T extends DateRangeSelectorPresetKey> {
    rangeKey: DateRangeKey<T>;
    presets: T[];
    fromDate: Date | null;
    toDate: Date | null;
    hasValidDateRange: boolean;
    onRangeChange: (key: DateRangeKey<T>) => void;
    onFromDateChange: (date: Date | null) => void;
    onToDateChange: (date: Date | null) => void;
}

const formatInputDate = (date: Date | null): string => (date ? format(date, 'yyyy-MM-dd') : '');

const parseInputDate = (value: string): Date | null => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
};

const DateRangeSelector = <T extends DateRangeSelectorPresetKey>({
    rangeKey,
    presets,
    fromDate,
    toDate,
    hasValidDateRange,
    onRangeChange,
    onFromDateChange,
    onToDateChange,
}: DateRangeSelectorProps<T>) => {
    const { translate } = useTranslate();
    const labelId = useId();

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: 0,
                alignItems: 'flex-start',
            }}
        >
            <FormControl
                size="small"
                sx={{ width: { xs: '100%', sm: 170 }, minWidth: { sm: 170 } }}
            >
                <InputLabel id={labelId}>{translate('dateRange')}</InputLabel>
                <Select<DateRangeKey<T>>
                    labelId={labelId}
                    value={rangeKey}
                    label={translate('dateRange')}
                    onChange={(event) => onRangeChange(event.target.value as DateRangeKey<T>)}
                >
                    {presets.map((preset) => (
                        <MenuItem key={preset} value={preset}>
                            {translate(PRESET_LABEL_KEYS[preset])}
                        </MenuItem>
                    ))}
                    <MenuItem value="custom">{translate('customRange')}</MenuItem>
                </Select>
            </FormControl>

            {rangeKey === 'custom' ? (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
                >
                    <TextField
                        size="small"
                        type="date"
                        label={translate('fromDate')}
                        value={formatInputDate(fromDate)}
                        onChange={(event) => onFromDateChange(parseInputDate(event.target.value))}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={!hasValidDateRange}
                        sx={{ width: { xs: '100%', sm: 155 } }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label={translate('toDate')}
                        value={formatInputDate(toDate)}
                        onChange={(event) => onToDateChange(parseInputDate(event.target.value))}
                        slotProps={{ inputLabel: { shrink: true } }}
                        helperText={hasValidDateRange ? undefined : translate('customRangeInvalid')}
                        error={!hasValidDateRange}
                        sx={{ width: { xs: '100%', sm: 155 } }}
                    />
                </Stack>
            ) : null}
        </Stack>
    );
};

export default DateRangeSelector;
